import type { ResourceFilters } from "@/lib/resource-filters"
import { filterResources } from "@/lib/resource-filters"
import { RESOURCE_CATEGORIES } from "@/lib/resources-data"

interface ResourceFilterTool {
    name: string
    description: string
    inputSchema: Record<string, unknown>
    execute: (input: unknown) => Promise<string>
}

// Keep the experimental browser API's types local to this integration.
export interface ResourceModelContext {
    registerTool: (
        tool: ResourceFilterTool,
        options: { signal: AbortSignal }
    ) => Promise<void> | void
    unregisterTool?: (name: string) => void
}

const CATEGORY_IDS = ["all", ...RESOURCE_CATEGORIES.map(({ id }) => id)]

function parseFilters(input: unknown): ResourceFilters {
    if (typeof input !== "object" || input === null || Array.isArray(input)) {
        throw new Error(
            "Expected an object with query, category, and dailyOnly."
        )
    }

    const values = input as Record<string, unknown>
    if (
        Object.keys(values).some(
            (key) => !["query", "category", "dailyOnly"].includes(key)
        )
    ) {
        throw new Error("Only query, category, and dailyOnly are supported.")
    }
    const { query = "", category = "all", dailyOnly = false } = values
    if (typeof query !== "string" || typeof dailyOnly !== "boolean") {
        throw new Error(
            "query must be a string and dailyOnly must be a boolean."
        )
    }
    if (typeof category !== "string" || !CATEGORY_IDS.includes(category)) {
        throw new Error(`category must be one of: ${CATEGORY_IDS.join(", ")}.`)
    }
    return { query: query.trim(), category, dailyOnly }
}

export function registerResourceFilterTool(
    context: ResourceModelContext | undefined,
    applyFilters: (filters: ResourceFilters) => void
) {
    const controller = new AbortController()
    let legacyRegistered = false
    if (typeof context?.registerTool !== "function") {
        return () => controller.abort()
    }

    const tool: ResourceFilterTool = {
        name: "filter_resources",
        description:
            "Search Mantha's curated resources and update the visible search, category, and everyday-stack filters. Returns matching names, descriptions, categories, and URLs. Search matches words in resource titles, descriptions, or category titles. Omitted filters reset to defaults; pass {} to show everything.",
        inputSchema: {
            type: "object",
            properties: {
                query: {
                    type: "string",
                    description:
                        "Search text; an empty string shows all matches.",
                    default: "",
                },
                category: {
                    type: "string",
                    enum: CATEGORY_IDS,
                    description: RESOURCE_CATEGORIES.map(
                        ({ id, title }) => `${id}: ${title}`
                    ).join("; "),
                    default: "all",
                },
                dailyOnly: {
                    type: "boolean",
                    description:
                        "Only show resources in Mantha's everyday stack.",
                    default: false,
                },
            },
            additionalProperties: false,
        },
        execute: async (input) => {
            controller.signal.throwIfAborted()
            const filters = parseFilters(input)
            const resources = filterResources(filters).flatMap((category) =>
                category.items.map((item) => ({
                    id: item.id,
                    name: item.title,
                    description: item.description,
                    category: category.id,
                    url: item.href,
                    daily: item.badge === "daily",
                }))
            )
            applyFilters(filters)
            return JSON.stringify({
                filters,
                count: resources.length,
                resources,
            })
        },
    }

    try {
        const registration = context.registerTool(tool, {
            signal: controller.signal,
        })
        legacyRegistered =
            registration === undefined && !!context.unregisterTool
        Promise.resolve(registration).catch((error: unknown) => {
            if (!controller.signal.aborted) {
                console.error(
                    "WebMCP resource tool registration failed:",
                    error
                )
            }
        })
    } catch (error) {
        console.error("WebMCP resource tool registration failed:", error)
    }

    return () => {
        controller.abort()
        // Chrome's earlier navigator API uses synchronous unregisterTool.
        if (legacyRegistered) {
            legacyRegistered = false
            try {
                context.unregisterTool?.(tool.name)
            } catch (error) {
                console.error("WebMCP resource tool cleanup failed:", error)
            }
        }
    }
}
